import * as React from "react";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Snackbar from "@mui/material/Snackbar";
import Tooltip from "@mui/material/Tooltip";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import { DarkMode } from "./DarkMode";
import TextField from "@mui/material/TextField";
import { trackEvent } from "../utils/gtag";
import { ACTION, EVENT } from "../utils/analytics-events";

interface HeaderProps {
  onReset: () => void;
  title: string;
  onTitleChange: (value: string) => void;
}

export const Header: React.FC<HeaderProps> = ({
  onReset,
  title,
  onTitleChange,
}) => {
  const [resetDone, setResetDone] = React.useState(false);

  React.useEffect(() => {
    if (!resetDone) return undefined;
    const timeout = window.setTimeout(() => setResetDone(false), 2500);
    return () => window.clearTimeout(timeout);
  }, [resetDone]);

  return (
    <Toolbar>
      <Tooltip title={resetDone ? "State reset" : "Reset state"}>
        <IconButton
          aria-label="Reset state"
          color="inherit"
          data-testid="resetState"
          edge="start"
          size="large"
          sx={{ mr: 2 }}
          onClick={() => {
            trackEvent(EVENT.SETTINGS, { action: ACTION.RESET_STATE });
            onReset();
            setResetDone(true);
          }}
        >
          <AccountBalanceIcon
            data-testid="accountBalanceIcon"
            sx={{
              animation: resetDone ? "resetStatePulse 700ms ease-out" : "none",
              "@keyframes resetStatePulse": {
                "0%": {
                  opacity: 1,
                  transform: "scale(1)",
                },
                "55%": {
                  opacity: 0.75,
                  transform: "scale(1.45)",
                },
                "100%": {
                  opacity: 1,
                  transform: "scale(1)",
                },
              },
              "@media (prefers-reduced-motion: reduce)": {
                animation: "none",
              },
            }}
          />
        </IconButton>
      </Tooltip>
      <TextField
        fullWidth
        InputProps={{ disableUnderline: true }}
        inputProps={{ "aria-label": "Header title" }}
        placeholder="Enter title"
        sx={{
          flexGrow: 1,
          mr: 2,
          "& .MuiInputBase-root:before, & .MuiInputBase-root:after": {
            borderBottom: "none !important",
          },
          "& .MuiInputBase-root:hover:not(.Mui-disabled, .Mui-error):before": {
            borderBottom: "none",
          },
          "& .MuiInputBase-input": {
            color: "#fff",
          },
        }}
        value={title}
        variant="standard"
        onChange={(e) => {
          trackEvent(EVENT.SETTINGS, { action: ACTION.CUSTOM_TITLE });
          onTitleChange(e.target.value);
        }}
      />
      <DarkMode />
      <Snackbar
        autoHideDuration={2500}
        message="State reset"
        open={resetDone}
        onClose={() => setResetDone(false)}
      />
    </Toolbar>
  );
};

export default Header;
